/**
 * PublicReglementArticle.tsx
 * Route : /reglements/:slugCode/:numero
 * Page détail d'un article de règlement européen
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { PublicHeader, PublicFooter, MCPBadge } from '@/components/jurisprudence';
import { Loader } from '@/components/Loader';

const SITE_URL = 'https://smartlawyer.ai';

interface ArticleDetail {
  celex:      string;
  slug_code:  string;
  slug:       string;
  numero:     string;
  code:       string;
  date:       string;
  text:       string;
  eurlex_url: string;
  source:     string;
}

function formatDate(d: string) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return d; }
}

function ArticleText({ text }: { text: string }) {
  // Première ligne = titre de l'article (souvent "Nom de l'article\n 1. ...")
  const lines   = text.split('\n');
  const title   = lines[0].trim();
  const body    = lines.slice(1).join('\n').trim();

  return (
    <div>
      {title && (
        <p className="font-semibold text-[15px] text-foreground mb-4">{title}</p>
      )}
      <div className="text-[14px] text-foreground/90 leading-relaxed whitespace-pre-wrap font-serif">
        {body || text}
      </div>
    </div>
  );
}

export default function PublicReglementArticle() {
  const { slugCode, numero } = useParams<{ slugCode: string; numero: string }>();
  const { t } = useTranslation();
  const [article, setArticle]   = useState<ArticleDetail | null>(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slugCode || !numero) return;
    setLoading(true);
    setNotFound(false);

    fetch(`/api/reglements/${encodeURIComponent(slugCode)}/${encodeURIComponent(numero)}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then(data => { if (data) setArticle(data); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slugCode, numero]);

  // Navigation prev/next basique par numéro
  const prevNum = numero ? String(Number(numero) - 1) : null;
  const nextNum = numero ? String(Number(numero) + 1) : null;

  const title = article
    ? `Art. ${article.numero} — ${article.code.slice(0, 60)}… | SmartLawyer`
    : t('reglements.article.fallback_title');

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={article ? t('reglements.article.meta_description', { numero: article.numero, code: article.code }) : ''} />
        {article && <link rel="canonical" href={`${SITE_URL}/reglements/${article.slug_code}/${article.numero}`} />}
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-8">

          {/* ── Breadcrumb ── */}
          <nav className="flex items-center gap-2 text-[12px] text-muted-foreground mb-6">
            <Link to="/reglements" className="hover:text-foreground transition-colors">
              🇪🇺 {t('reglements.breadcrumb')}
            </Link>
            {article && (
              <>
                <span>/</span>
                <span className="text-foreground font-medium truncate max-w-[300px]">
                  CELEX:{article.celex}
                </span>
              </>
            )}
          </nav>

          {/* ── Loading ── */}
          {loading && (
            <div className="flex justify-center py-24">
              <Loader className="h-8 w-8" />
            </div>
          )}

          {/* ── Not found ── */}
          {!loading && notFound && (
            <div className="text-center py-24">
              <p className="text-5xl mb-4">🔎</p>
              <p className="font-semibold text-foreground mb-2">{t('articles.not_found_title')}</p>
              <p className="text-sm text-muted-foreground mb-6">
                {t('reglements.article.not_found_text', { numero, slug: slugCode })}
              </p>
              <Link to="/reglements" className="text-primary font-medium hover:underline text-sm">
                {t('articles.back_to_search')}
              </Link>
            </div>
          )}

          {/* ── Article ── */}
          {!loading && article && (
            <article>
              {/* En-tête */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
                    🇪🇺 {t('articles.heading', { numero: article.numero })}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5">
                    {article.celex}
                  </span>
                  {article.date && (
                    <span className="text-[10px] text-muted-foreground">
                      {formatDate(article.date)}
                    </span>
                  )}
                </div>

                <h1 className="font-serif text-[22px] sm:text-[26px] font-bold leading-tight text-foreground mb-2">
                  {t('articles.heading', { numero: article.numero })}
                </h1>
                <p className="text-[13px] text-muted-foreground leading-snug">
                  {article.code}
                </p>
              </div>

              {/* Séparateur */}
              <div className="h-px bg-border mb-6" />

              {/* Corps de l'article */}
              <div className="bg-muted/20 rounded-xl border border-border p-5 sm:p-7 mb-6">
                <ArticleText text={article.text} />
              </div>

              {/* Liens */}
              <div className="flex items-center justify-between gap-4 mb-8">
                <a
                  href={article.eurlex_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  {t('reglements.article.view_eurlex')}
                </a>

                <Link
                  to="/reglements"
                  className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('articles.back_to_search')}
                </Link>
              </div>

              {/* Navigation prev/next */}
              <div className="flex justify-between gap-4 pt-4 border-t border-border">
                {prevNum && Number(prevNum) > 0 ? (
                  <Link
                    to={`/reglements/${slugCode}/${prevNum}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:border-primary/40 hover:bg-accent text-[13px] font-medium transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t('articles.heading', { numero: prevNum })}
                  </Link>
                ) : <div />}

                {nextNum && (
                  <Link
                    to={`/reglements/${slugCode}/${nextNum}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:border-primary/40 hover:bg-accent text-[13px] font-medium transition-all"
                  >
                    {t('articles.heading', { numero: nextNum })}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                )}
              </div>
            </article>
          )}
        </main>

        <PublicFooter />
      </div>
      <MCPBadge />
    </>
  );
}
